import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import { 
  useAddToCMPMutation, 
  useLazyCheckLeadInCMPQuery,
  useRemoveFromCMPMutation 
} from "../../../features/cmpSlice";
import { toast } from "react-hot-toast";
import { Modal, TextField, Button, Typography, Box } from "@mui/material";
import moment from "moment";

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 2em;
  min-width: 500px;
  max-width: 600px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  outline: none;
`;

const Header = styled.div`
  margin-bottom: 1.5em;
  padding-bottom: 1em;
  border-bottom: 2px solid ${colors.inputBorderWhite};
  
  h2 {
    margin: 0;
    color: ${colors.textMed};
  }
  
  p {
    margin: 0.5em 0 0 0;
    color: ${colors.textLight};
    font-size: 0.9rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 1.5em;
  
  label {
    display: block;
    margin-bottom: 0.5em;
    color: ${colors.textMed};
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1em;
  justify-content: flex-end;
  margin-top: 2em;
  padding-top: 1.5em;
  border-top: 1px solid ${colors.inputBorderWhite};
`;

export const AddToCMPModal = ({ open, onClose, lead }) => {
  const [remarks, setRemarks] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [isInCMP, setIsInCMP] = useState(false);
  const [existingCMP, setExistingCMP] = useState(null);

  const [addToCMP, { isLoading: isAdding }] = useAddToCMPMutation();
  const [removeFromCMP, { isLoading: isRemoving }] = useRemoveFromCMPMutation();
  const [checkLeadInCMP] = useLazyCheckLeadInCMPQuery();

  useEffect(() => {
    if (open && lead?.id) {
      checkLeadInCMP(lead.id).then((result) => {
        const inCMP = result.data?.isInCMP || false;
        setIsInCMP(inCMP);
        setExistingCMP(result.data?.cmp || null);
        if (inCMP && result.data?.cmp) {
          setRemarks(result.data.cmp.remarks || "");
          setMeetingDate(
            result.data.cmp.meetingDate
              ? moment(result.data.cmp.meetingDate).format("YYYY-MM-DD")
              : ""
          );
        } else {
          setRemarks("");
          setMeetingDate("");
        }
      });
    }
  }, [open, lead]);

  const handleSubmit = async () => {
    if (!lead) return;

    try {
      if (isInCMP && existingCMP) {
        toast.error("Lead is already in CMP. Remove it first to add again.");
        return;
      }

      await addToCMP({
        leadId: lead.id,
        remarks,
        meetingDate: meetingDate || null,
      }).unwrap();
      
      toast.success("Lead added to CMP successfully");
      onClose();
      setRemarks("");
      setMeetingDate("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add lead to CMP");
    }
  };

  const handleRemove = async () => {
    if (!existingCMP?._id) return;

    try {
      await removeFromCMP(existingCMP._id).unwrap();
      toast.success("Lead removed from CMP");
      setIsInCMP(false);
      setExistingCMP(null);
      setRemarks("");
      setMeetingDate("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to remove from CMP");
    }
  };

  if (!lead) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Header>
          <h2>{isInCMP ? "Update CMP Entry" : "Add Lead to CMP"}</h2>
          <p>
            Client: <strong>{lead.client}</strong> | Lead ID: <strong>{lead.leadId}</strong>
          </p>
        </Header>

        <FormSection>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add your remarks about this lead's conversion potential..."
            disabled={isInCMP && !existingCMP}
          />
        </FormSection>

        <FormSection>
          <TextField
            fullWidth
            type="date"
            label="Meeting Date"
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isInCMP && !existingCMP}
          />
        </FormSection>

        {isInCMP && existingCMP && (
          <Box
            sx={{
              padding: "1em",
              backgroundColor: "#fef3c7",
              borderRadius: "6px",
              marginBottom: "1em",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              This lead is already in your CMP for this month. Remove it first to add again.
            </Typography>
          </Box>
        )}

        <ButtonGroup>
          {isInCMP && existingCMP && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemove}
              disabled={isRemoving}
            >
              {isRemoving ? "Removing..." : "Remove from CMP"}
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          {!isInCMP && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isAdding}
              style={{ background: colors.brown }}
            >
              {isAdding ? "Adding..." : "Add to CMP"}
            </Button>
          )}
        </ButtonGroup>
      </ModalContainer>
    </Modal>
  );
};
